import { getSelectedCompany } from "../utils/auth";
import { getAdminId, getRegisterId } from "../utils/authId";

const frontendUrl = process.env.REACT_APP_FRONTEND_SOCKET_URL;

class WebSocketOnline {
  static instance = null;
  socket = null;
  reconnectAttempts = 0;
  maxReconnectAttempts = 5;
  reconnectTimeout = null;
  onlineStatusInterval = null;

  connectionState = {
    isConnecting: false,
    isConnected: false,
    lastToken: null,
  };

  callbacks = {
    onmessage: null,
    onclose: null,
    onerror: null,
    onconnect: null,
    onreconnect: null,
    ondisconnect: null,
  };

  constructor() {
    if (WebSocketOnline.instance) {
      return WebSocketOnline.instance;
    }
    WebSocketOnline.instance = this;

    if (typeof window !== "undefined") {
      window.addEventListener(
        "beforeunload",
        this.handleBrowserClose.bind(this)
      );
    }
  }

  connect(token) {
    // Validate token
    if (!token) {
      console.error("No token provided for WebSocket connection");
      return;
    }

    // Prevent duplicate connections
    if (this.connectionState.isConnecting) {
      console.log("Connection already in progress...");
      return;
    }

    if (this.socket?.readyState === WebSocket.OPEN) {
      console.log("WebSocket already connected");
      return;
    }

    // Clean up existing connection
    this.cleanup();

    this.connectionState.isConnecting = true;
    this.connectionState.lastToken = token;

    const userId = getRegisterId();
    const admin_id = getAdminId();
    const companyId = getSelectedCompany();

    if (!userId) {
      console.error("User ID not found");
      this.connectionState.isConnecting = false;
      return;
    }

    try {
      // Construct WebSocket URL properly
      const wsUrl = `${frontendUrl}/ws/online_consumer/${userId}/?token=${encodeURIComponent(
        token
      )}`;
      this.socket = new WebSocket(wsUrl);

      this.setupEventHandlers(admin_id, companyId?.id);
    } catch (error) {
      console.error("WebSocket connection failed:", error);
      this.connectionState.isConnecting = false;
      this.handleReconnection(token);
    }
  }

  setupEventHandlers(admin_id, company_id) {
    this.socket.onopen = () => {
      console.log("WebSocket connected successfully");
      this.connectionState.isConnecting = false;
      this.connectionState.isConnected = true;
      this.reconnectAttempts = 0;

      // Send initial open message
      this.sendOpenMessage(admin_id, company_id);

      // Start online status interval
      this.startOnlineStatusInterval(admin_id, company_id);

      // Trigger appropriate callback
      const isReconnect = this.reconnectAttempts > 0;
      if (isReconnect) {
        this.callbacks.onreconnect?.();
      } else {
        this.callbacks.onconnect?.();
      }
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.callbacks.onmessage?.(data);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    this.socket.onclose = (event) => {
      console.log("WebSocket closed:", event.code, event.reason);
      this.connectionState.isConnecting = false;
      this.connectionState.isConnected = false;

      this.callbacks.onclose?.(event);
      this.callbacks.ondisconnect?.();

      // Handle reconnection for unexpected closures
      if (
        event.code !== 1000 &&
        this.reconnectAttempts < this.maxReconnectAttempts
      ) {
        this.handleReconnection(this.connectionState.lastToken);
      }
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      this.connectionState.isConnecting = false;
      this.callbacks.onerror?.(error);
    };
  }

  sendOpenMessage(admin_id, company_id) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(
          JSON.stringify({
            type: "open",
            admin_id: admin_id,
            company_id: company_id,
          })
        );
      } catch (error) {
        console.error("Error sending open message:", error);
      }
    }
  }

  startOnlineStatusInterval(admin_id, company_id) {
    // Clear existing interval
    if (this.onlineStatusInterval) {
      clearInterval(this.onlineStatusInterval);
    }

    // Send online status every 10 seconds
    this.onlineStatusInterval = setInterval(() => {
      this.sendOnlineStatus(admin_id, company_id);
    }, 10000);
  }

  sendOnlineStatus(admin_id = null, company_id = null) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      try {
        const adminId = admin_id || getAdminId();
        const companyId = company_id || getSelectedCompany()?.id;

        this.socket.send(
          JSON.stringify({
            type: "online",
            admin_id: adminId,
            company_id: companyId,
          })
        );
      } catch (error) {
        console.error("Error sending online status:", error);
      }
    }
  }

  handleReconnection(token) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      return;
    }

    // Clear existing timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    const timeout = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      console.log(`Reconnection attempt ${this.reconnectAttempts}`);
      this.connect(token);
    }, timeout);
  }

  disconnect() {
    console.log("Disconnecting WebSocket...");

    // Clear all intervals and timeouts
    this.cleanup();

    // Send close message if connected
    if (this.socket?.readyState === WebSocket.OPEN) {
      try {
        const admin_id = getAdminId();
        const companyId = getSelectedCompany()?.id;

        this.socket.send(
          JSON.stringify({
            type: "close",
            admin_id: admin_id,
            company_id: companyId,
          })
        );
      } catch (error) {
        console.error("Error sending close message:", error);
      }

      this.socket.close(1000, "User initiated disconnect");
    }

    this.socket = null;
    this.connectionState.isConnecting = false;
    this.connectionState.isConnected = false;
    this.callbacks.ondisconnect?.();
  }

  cleanup() {
    // Clear intervals
    if (this.onlineStatusInterval) {
      clearInterval(this.onlineStatusInterval);
      this.onlineStatusInterval = null;
    }

    // Clear timeouts
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  handleBrowserClose() {
    this.disconnect();
  }

  // Public method to get connection status
  getConnectionStatus() {
    return {
      isConnected: this.connectionState.isConnected,
      isConnecting: this.connectionState.isConnecting,
      readyState: this.socket?.readyState,
    };
  }

  // Callback registration
  setCallback(type, fn) {
    if (this.callbacks.hasOwnProperty(type)) {
      this.callbacks[type] = fn;
    } else {
      console.warn(`Invalid callback type: ${type}`);
    }
  }

  // Remove callback
  removeCallback(type) {
    if (this.callbacks.hasOwnProperty(type)) {
      this.callbacks[type] = null;
    }
  }
}

// Create and export singleton instance
const webSocketOnline = new WebSocketOnline();
export default webSocketOnline;
